























import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
import { interpolate } from 'd3-interpolate';
import { scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { arc } from 'd3-shape';
import 'd3-transition';
import { KpiEnum, OrientationEnum } from '../../enums';

export default {
  name: 'kpi-summary',
  computed: {
    kpiSummaryClasses: function (): string[] {
      return [
        `epb-kpi-summary--${ this.id }`,
        `epb-kpi-summary--${ this.orientation }`,
        this.isVertical ? 'epb-kpi-summary--vertical' : 'epb-kpi-summary--horizontal'
      ];
    },
    percentageOfGrowth: function (): number {
      const mainPeriodPercentage = this.percentages[0];
      const comparePeriodPercentage = this.percentages[1];
      const increaseValue = mainPeriodPercentage - comparePeriodPercentage;
      return Math.round((increaseValue / (comparePeriodPercentage || 1)) * 100);
    },
    compareArrowIconClasses: function (): string[] {
      const classes = [this.valueColorClass];
      if (this.percentageOfGrowth > 0) {
        classes.push('epb-kpi-summary__icon--arrow-up');
      }
      return classes;
    },
    valueColorClass: function (): string {
      const positiveClass = 'epb-kpi-summary__icon--positive';
      const negativeClass = 'epb-kpi-summary__icon--negative';
      if (this.id !== KpiEnum.NO_RESULTS) {
        return this.percentageOfGrowth > 0 ? positiveClass : negativeClass;
      } else {
        return this.percentageOfGrowth < 0 ? positiveClass : negativeClass;
      }
    },
    isVertical: function (): boolean {
      return (this.orientation === OrientationEnum.TOP || this.orientation === OrientationEnum.BOTTOM);
    },
    chartCenterPosition: function () {
      let x: number, y: number;
      if (this.isVertical) {
        x = this.chartSize.width / 2;
        y = this.chartSize.height;
      } else {
        x = 0;
        y = this.chartSize.width;
      }
      return { x, y };
    },
    arcMinRadius: function (): number {
      return this.chartCore.radius + this.chartCore.padding;
    }
  },
  data: function () {
    return {
      chartElement: undefined,
      chartSize: {
        width: 0,
        height: 0
      },
      percentageScale: scaleLinear().domain([0, 100]),
      percentageArc: arc(),
      resizeObserver: undefined
    };
  },
  props: {
    id: {
      type: String,
      required: true
    },
    percentages: { // [mainPeriod: number, compPeriod: number]
      type: Array,
      required: true
    },
    chartCore: { // TODO not reactive yet, should be in a future refactor
      type: Object,
      default: () => ({
        radius: 5,
        padding: 5
      })
    },
    label: String,
    orientation: {
      type: String,
      default: OrientationEnum.TOP
    }
  },
  methods: {
    getPercentageText: function (percentage: number): string {
      return percentage.toLocaleString(this.$epb.locale, { maximumFractionDigits: 2 });
    },
    initializeChart: function (): void {
      this.chartElement = select(this.$el);
      this.chartElement
        .select('.epb-kpi-summary__chart-arc')
        .datum({ outerRadius: this.arcMinRadius });
      this.percentageArc.innerRadius(this.arcMinRadius);
    },
    renderChart: function (): void {
      this.percentageScale
        .range([this.arcMinRadius, this.isVertical ? this.chartSize.height : this.chartSize.width]);

      this.percentageArc
        .startAngle(this.isVertical ? -Math.PI / 2 : 0)
        .endAngle(this.isVertical ? Math.PI / 2 : Math.PI);

      this.chartElement
        .select('.epb-kpi-summary__chart')
        .attr('width', this.chartSize.width)
        .attr('height', this.chartSize.height);

      this.chartElement
        .select('.epb-kpi-summary__chart-core')
        .attr('cx', this.chartCenterPosition.x)
        .attr('cy', this.chartCenterPosition.y);

      this.chartElement
        .select('.epb-kpi-summary__chart-arc')
        .style('transform', `translate3d(${ this.chartCenterPosition.x }px,${ this.chartCenterPosition.y }px, 0)`)
        .transition().duration(500)
        .attrTween('d', this.arcTween(Math.min((this.percentages[0] || 0), 100)));
    },
    arcTween: function (outerRadius: number) {
      return (d: any) => {
        const i = interpolate(d.outerRadius, this.percentageScale(outerRadius));
        return (t: any) => {
          d.outerRadius = i(t);
          return this.percentageArc(d);
        };
      };
    }
  },
  mounted(): void {
    const ResizeObserver = (window['ResizeObserver'] || ResizeObserverPolyfill);
    this.resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (this.isVertical) {
          const { width } = entry.contentRect;
          this.chartSize = { width, height: width / 2 };
        } else {
          const { height } = entry.contentRect;
          this.chartSize = { width: height / 2, height };
        }
      });
    });
    this.resizeObserver.observe(this.$el);
    this.initializeChart();
  },
  beforeDestroy(): void {
    this.resizeObserver.disconnect();
  },
  watch: {
    percentages: function (): void {
      this.renderChart();
    },
    chartSize: function (): void {
      this.renderChart();
    }
  }
};
